import { ROUTES, type RoutePath } from './lib/routes';
import { getPortalToken, isPortalAdmin, setLoginNext } from './lib/portalSession';

type Handler = () => void;

const registry = new Map<string, Handler>();

export function register(path: RoutePath, handler: Handler): void {
  registry.set(path, handler);
}

export function navigate(path: RoutePath): void {
  if (window.location.pathname !== path) {
    window.history.pushState({ path }, '', path);
  }
  dispatch();
}

/** 운영 nginx가 `/` → `/home` 인 경우 주소창은 `/home` 유지, 내부 라우팅만 `/`와 동일 처리 */
function logicalPath(pathname: string): string {
  const p = normalizePath(pathname);
  if (p === '/home') return ROUTES.home;
  if (p.startsWith('/home/')) {
    const rest = p.slice('/home'.length);
    return normalizePath(rest.startsWith('/') ? rest : `/${rest}`);
  }
  return p;
}

function needsAuth(path: string): boolean {
  return path === ROUTES.myApplications || path === ROUTES.myProfile;
}

function needsAdmin(path: string): boolean {
  return (
    path === ROUTES.manage ||
    path === ROUTES.manageResources ||
    path === ROUTES.manageInstallCases
  );
}

function dispatch(): void {
  let path = logicalPath(window.location.pathname);
  const known = new Set<string>([
    ROUTES.home,
    ROUTES.newInstall,
    ROUTES.consult,
    ROUTES.differentiatedServices,
    ROUTES.installCaseBoard,
    ROUTES.resources,
    ROUTES.existing,
    ROUTES.evostDemo,
    ROUTES.serviceApply,
    ROUTES.signup,
    ROUTES.login,
    ROUTES.myApplications,
    ROUTES.myProfile,
    ROUTES.manage,
    ROUTES.manageResources,
    ROUTES.manageInstallCases,
  ]);
  if (!known.has(path)) {
    window.history.replaceState({ path: ROUTES.homePublic }, '', ROUTES.homePublic);
    path = ROUTES.home;
  }
  if (needsAuth(path) && !getPortalToken()) {
    setLoginNext(path);
    window.history.replaceState({ path: ROUTES.login }, '', ROUTES.login);
    path = ROUTES.login;
  }
  if (needsAdmin(path)) {
    if (!getPortalToken()) {
      setLoginNext(path);
      window.history.replaceState({ path: ROUTES.login }, '', ROUTES.login);
      path = ROUTES.login;
    } else if (!isPortalAdmin()) {
      window.history.replaceState({ path: ROUTES.homePublic }, '', ROUTES.homePublic);
      path = ROUTES.home;
    }
  }
  const handler = registry.get(path) ?? registry.get(ROUTES.home);
  handler?.();
}

function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname || ROUTES.home;
}

export function startRouter(): void {
  window.addEventListener('popstate', () => dispatch());
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement | null;
    const a = t?.closest?.('a[data-nav]') as HTMLAnchorElement | null;
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || !href.startsWith('/') || href.startsWith('//')) return;
    e.preventDefault();
    navigate(href as RoutePath);
  });
  dispatch();
}
